/* eslint-disable @typescript-eslint/naming-convention */
import {env} from '@matic/config/env';

export type ProductEventName =
  | 'session.started'
  | 'session.heartbeat'
  | 'page.viewed'
  | 'api.completed'
  | 'api.failed'
  | 'ai.scout.prompt_submitted'
  | 'ai.scout.response_completed'
  | 'ai.scribe.prompt_submitted'
  | 'ai.scribe.response_completed'
  | 'ai.scribe.scout_imported'
  | 'ai.otto.prompt_submitted'
  | 'ai.otto.response_completed'
  | 'ai.otto.view_stream_failed'
  | 'resource.created'
  | 'resource.updated'
  | 'shortcut.created'
  | 'shortcut.opened'
  | 'quoting.citation.highlighted';

export type ProductEventProperties = Record<string, unknown>;

type ProductEvent = {
  event_name: ProductEventName;
  timestamp: string;
  session_id: string;
  route?: string;
  properties: ProductEventProperties;
};

const SESSION_STORAGE_KEY = 'matic_product_observability_session_id';
const MAX_BATCH_SIZE = 20;
const FLUSH_INTERVAL_MS = 10_000;
const HEARTBEAT_INTERVAL_MS = 120_000;
const MAX_STRING_LENGTH = 4000;

let eventQueue: ProductEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | undefined;
let sessionStarted = false;
let heartbeatStarted = false;
let flushInFlight = false;

function isEnabled(): boolean {
  return Boolean(
    env.ENABLE_PRODUCT_OBSERVABILITY && globalThis.window !== undefined
  );
}

function getCurrentRoute(): string | undefined {
  if (globalThis.window === undefined) return undefined;
  return `${globalThis.location.pathname}${globalThis.location.search}`;
}

export function getProductObservabilitySessionId(): string {
  if (globalThis.window === undefined) {
    return 'server-session';
  }

  const existing = globalThis.sessionStorage.getItem(SESSION_STORAGE_KEY);
  if (existing) return existing;

  const generated =
    globalThis.crypto?.randomUUID?.() ??
    `session-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  globalThis.sessionStorage.setItem(SESSION_STORAGE_KEY, generated);
  return generated;
}

function sanitizeValue(value: unknown, depth = 0): unknown {
  if (depth >= 6) return '[truncated:max_depth]';
  if (value === null || value === undefined) return value;
  if (typeof value === 'boolean' || typeof value === 'number') return value;
  if (typeof value === 'string') return value.slice(0, MAX_STRING_LENGTH);
  if (Array.isArray(value)) {
    return value.slice(0, 100).map((item) => sanitizeValue(item, depth + 1));
  }

  if (typeof value === 'object') {
    const result: ProductEventProperties = {};
    for (const [key, item] of Object.entries(value)) {
      result[key.slice(0, 256)] = sanitizeValue(item, depth + 1);
    }

    return result;
  }

  if (typeof value === 'bigint') return value.toString();
  if (typeof value === 'symbol') return value.description ?? value.toString();
  if (typeof value === 'function') return value.name || '[function]';

  return '[unserializable]';
}

function sanitizeProperties(
  properties: ProductEventProperties = {}
): ProductEventProperties {
  return sanitizeValue(properties) as ProductEventProperties;
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  try {
    const auth = await import('@matic/lib/auth/oidc-token');
    return await auth.getAuthHeaders();
  } catch {
    return {};
  }
}

function scheduleFlush(): void {
  if (flushTimer !== undefined || !isEnabled()) return;
  flushTimer = setTimeout(() => {
    flushTimer = undefined;
    void flushProductEvents();
  }, FLUSH_INTERVAL_MS);
}

function ensureSessionStarted(): void {
  if (sessionStarted || !isEnabled()) return;
  sessionStarted = true;
  enqueueProductEvent('session.started', {
    route: getCurrentRoute(),
    userAgent: globalThis.navigator?.userAgent,
    viewport: getViewport(),
  });
  startProductObservabilityHeartbeat();
}

function enqueueProductEvent(
  eventName: ProductEventName,
  properties: ProductEventProperties = {},
  route = getCurrentRoute()
): void {
  eventQueue.push({
    event_name: eventName,
    timestamp: new Date().toISOString(),
    session_id: getProductObservabilitySessionId(),
    ...(route ? {route} : {}),
    properties: sanitizeProperties(properties),
  });

  if (eventQueue.length >= MAX_BATCH_SIZE) {
    void flushProductEvents();
  } else {
    scheduleFlush();
  }
}

export function trackProductEvent(
  eventName: ProductEventName,
  properties: ProductEventProperties = {},
  route = getCurrentRoute()
): void {
  if (!isEnabled()) return;
  if (eventName !== 'session.started') ensureSessionStarted();
  enqueueProductEvent(eventName, properties, route);
}

export async function flushProductEvents(): Promise<void> {
  if (!isEnabled() || flushInFlight || eventQueue.length === 0) return;
  flushInFlight = true;
  const batch = eventQueue.slice(0, MAX_BATCH_SIZE);
  eventQueue = eventQueue.slice(MAX_BATCH_SIZE);

  try {
    const headers = new Headers({
      'Content-Type': 'application/json',
      ...(await getAuthHeaders()),
    });
    const response = await fetch(`${env.API_URL}/api/v1/observability/events`, {
      method: 'POST',
      headers,
      body: JSON.stringify({events: batch}),
      keepalive: batch.length <= 10,
    });

    if (!response.ok) {
      throw new Error(`Observability ingest failed: ${response.status}`);
    }
  } catch {
    eventQueue = [...batch, ...eventQueue].slice(0, 200);
  } finally {
    flushInFlight = false;
    if (eventQueue.length > 0) scheduleFlush();
  }
}

export function startProductObservabilityHeartbeat(): void {
  if (heartbeatStarted || !isEnabled()) return;
  heartbeatStarted = true;
  globalThis.setInterval(() => {
    if (globalThis.document?.visibilityState === 'hidden') return;
    trackProductEvent('session.heartbeat', {
      route: getCurrentRoute(),
      visibilityState: globalThis.document?.visibilityState,
    });
  }, HEARTBEAT_INTERVAL_MS);

  globalThis.addEventListener('pagehide', () => {
    void flushProductEvents();
  });
}

/**
 * Route "shape" for a path — UUID segments replaced with `:id` — so analysis
 * can group "which kind of page" (e.g. an object-record detail, a record
 * detail) across many different visited records without every distinct id
 * fragmenting the grouping. The literal ids stay available via `resourceIds`
 * (from both the path and any query params, e.g. Library's `?ref=`/`?browse=`
 * or Triage's `?thread=`) for joining against `resource.*`/`api.*` events on
 * the same record.
 */
export function derivePagePattern(pathname: string): string {
  return pathname.replaceAll(
    /[\da-f]{8}(?:-[\da-f]{4}){3}-[\da-f]{12}/gi,
    ':id'
  );
}

export function trackPageViewed(pathname: string, previousPath?: string): void {
  const search = globalThis.location?.search ?? '';
  trackProductEvent(
    'page.viewed',
    {
      path: pathname,
      search,
      pattern: derivePagePattern(pathname),
      resourceIds: extractResourceIds(`${pathname}${search}`),
      previousPath,
      title: globalThis.document?.title,
      viewport: getViewport(),
    },
    pathname
  );
}

export function getViewport(): {width: number; height: number} | undefined {
  if (globalThis.window === undefined) return undefined;
  return {
    width: globalThis.window.innerWidth,
    height: globalThis.window.innerHeight,
  };
}

export function extractResourceIds(endpoint: string): string[] {
  const uuidPattern = /\b[\da-f]{8}(?:-[\da-f]{4}){3}-[\da-f]{12}\b/gi;
  return [...new Set(endpoint.match(uuidPattern) ?? [])];
}

export function summarizeResponseMetadata(
  data: unknown
): ProductEventProperties {
  if (!data || typeof data !== 'object') return {};
  const source = Array.isArray(data) ? {count: data.length} : data;
  const allowed = [
    'id',
    'display_id',
    'displayId',
    'name',
    'title',
    'resource_type',
    'resourceType',
    'total',
    'count',
  ];
  const result: ProductEventProperties = {};
  for (const key of allowed) {
    if (Object.hasOwn(source, key)) {
      result[key] = (source as Record<string, unknown>)[key];
    }
  }

  return result;
}

export function shouldSkipApiTelemetry(endpoint: string): boolean {
  return endpoint.includes('/api/v1/observability/events');
}
